import { fabric } from 'fabric'
import type WbDynamic from './dynamic'

interface Point {
  x: number
  y: number
}

interface Rectangle {
  cx: number // center x
  cy: number // center y
  width: number
  height: number
}

export class LinkedObjectsLines {
  canvas: fabric.Canvas
  ctx: CanvasRenderingContext2D
  selectedObject: any | null = null

  constructor(canvas: fabric.Canvas) {
    this.canvas = canvas
    this.ctx = canvas.getSelectionContext()
  }

  private watch() {
    this.canvas.on('after:render', () => {
      if (!this.selectedObject) { return }
      this.drawLines()
    })

    this.canvas.on('selection:created', (e) => {
      if (e.selected?.length !== 1) {
        this.selectedObject = null
        return
      }
      this.selectedObject = e.selected[0]
    })

    this.canvas.on('selection:updated', (e) => {
      if (e.selected?.length !== 1) {
        this.selectedObject = null
        return
      }
      this.selectedObject = e.selected[0]
    })

    this.canvas.on('selection:cleared', () => {
      this.selectedObject = null
    })
  }

  private drawLines() {
    if ((!this.selectedObject || !this.selectedObject.id) && !(this.selectedObject.data && this.selectedObject.data.isResource)) { return }
    if (this.selectedObject.canBeLinkedTo) {
      let targetObjId = this.selectedObject.id
      // Get all objects that are linked to this object
      if (this.selectedObject.dynamicGrid) {
        targetObjId = this.selectedObject.dynamicGrid.getChildObjectReference(this.selectedObject.id)
      }
      if (!targetObjId) { return }
      const linkedObjects = this.canvas.getObjects().filter((obj: any) => (obj.link && obj.link.id === targetObjId && obj.visible) || (obj.data && obj.data.link && obj.data.link.id === targetObjId && obj.visible))
      linkedObjects.forEach((obj: any) => {
        this.drawLineBetween(this.selectedObject, obj)
      })
    }
    else if (this.selectedObject.link?.id) {
      // Find the object that it is linked to
      if (this.selectedObject.link.id.startsWith('dg|')) {
        // Find the Dynamic Grid
        const gridId = this.selectedObject.link.id.split('|')[1]
        const grid = this.canvas.getObjects().find((obj: any) => obj.id === gridId) as WbDynamic
        if (grid) {
          const linkedObject = grid.getChildObjectByReference(this.selectedObject.link.id)
          if (linkedObject && linkedObject.visible) {
            this.drawLineBetween(this.selectedObject, linkedObject)
          }
        }
      }
      else {
        const linkedObject = this.canvas.getObjects().find((obj: any) => obj.id === this.selectedObject.link.id)
        if (linkedObject && linkedObject.visible) {
          this.drawLineBetween(this.selectedObject, linkedObject)
        }
      }
    }
    else if (this.selectedObject.data && this.selectedObject.data.isResource) { // resource
      const linkedObject = this.canvas.getObjects().find((obj: any) => obj.id === this.selectedObject.data.link.id)
      if (linkedObject && linkedObject.visible) {
        this.drawLineBetween(this.selectedObject, linkedObject)
      }
    }
  }

  private drawLineBetween(obj1: fabric.Object, obj2: fabric.Object) {
    const ctx = this.ctx
    ctx.save()

    ctx.strokeStyle = 'red'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 5])
    ctx.beginPath()

    const obj1Coords = obj1.getCenterPoint()
    const obj2Coords = obj2.getCenterPoint()

    const { intersectionA, intersectionB } = this.getRectangleIntersections({ cx: obj1Coords.x, cy: obj1Coords.y, width: obj1.width! * obj1.scaleX!, height: obj1.height! * obj1.scaleY! }, { cx: obj2Coords.x, cy: obj2Coords.y, width: obj2.width! * obj2.scaleX!, height: obj2.height! * obj2.scaleY! })

    const point1 = fabric.util.transformPoint(new fabric.Point(intersectionA.x, intersectionA.y), this.canvas.viewportTransform as any)
    const point2 = fabric.util.transformPoint(new fabric.Point(intersectionB.x, intersectionB.y), this.canvas.viewportTransform as any)

    ctx.moveTo(point1.x, point1.y)
    ctx.lineTo(point2.x, point2.y)

    ctx.stroke()
    ctx.restore()
  }

  getIntersection(
    cx: number,
    cy: number,
    w: number,
    h: number,
    dx: number,
    dy: number,
  ): Point {
    const hw = w / 2
    const hh = h / 2

    const scaleX = dx !== 0 ? hw / Math.abs(dx) : Infinity
    const scaleY = dy !== 0 ? hh / Math.abs(dy) : Infinity

    const scale = Math.min(scaleX, scaleY)

    return {
      x: cx + dx * scale,
      y: cy + dy * scale,
    }
  }

  getRectangleIntersections(
    rectA: Rectangle,
    rectB: Rectangle,
  ): { intersectionA: Point, intersectionB: Point } {
    const dx = rectB.cx - rectA.cx
    const dy = rectB.cy - rectA.cy

    const magnitude = Math.sqrt(dx * dx + dy * dy)
    const dirX = dx / magnitude
    const dirY = dy / magnitude

    const intersectionA = this.getIntersection(
      rectA.cx,
      rectA.cy,
      rectA.width,
      rectA.height,
      dirX,
      dirY,
    )

    const intersectionB = this.getIntersection(
      rectB.cx,
      rectB.cy,
      rectB.width,
      rectB.height,
      -dirX,
      -dirY,
    )

    return {
      intersectionA,
      intersectionB,
    }
  }

  public init() {
    this.watch()
  }
}
